import { useEffect } from 'react';

// SEO Configuration for each page
export const SEO_CONFIG = {
  home: {
    title: 'RAL Real Advisor | Commercial Transformation Platform for Real Estate Developers',
    description:
      'RAL Real Advisor helps real estate developers evaluate strategy, marketing, sales, pricing, CRM and team performance. Get a commercial maturity score, radar analysis and a 180-day optimisation plan.',
    keywords:
      'real estate commercial transformation, developer sales funnel optimisation, real estate GTM strategy, property developer diagnostic, real estate pricing strategy, developer CRM optimisation, real estate performance analytics',
    canonical: 'https://ralrealadvisor.com/',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "RAL Real Advisor",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "Commercial diagnostic platform helping real estate developers optimise strategy, marketing, sales and pricing performance.",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR",
        "description": "Starter commercial diagnostic assessment"
      },
      "provider": {
        "@type": "Organization",
        "name": "RAL Real Advisor"
      }
    }
  },

  about: {
    title: 'About RAL Real Advisor | Commercial Transformation for Real Estate Developers',
    description:
      'RAL Real Advisor helps real estate developers build predictable, scalable and high-performance commercial systems through strategy, GTM execution, pricing architecture and sales optimisation.',
    keywords:
      'real estate commercial strategy, developer GTM execution, property developer advisory, real estate sales optimisation, developer pricing strategy consulting',
    canonical: 'https://ralrealadvisor.com/about',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "RAL Real Advisor",
      "description": "Commercial transformation and performance advisory for real estate developers.",
      "url": "https://ralrealadvisor.com",
      "logo": "https://ralrealadvisor.com/logo.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "sales",
        "availableLanguage": ["English"]
      }
    }
  },

  features: {
    title: 'Features | Real Estate Commercial Diagnostic & Performance Platform',
    description:
      'Evaluate your real estate business across strategy, marketing, sales funnel performance, CRM discipline, pricing architecture and team KPIs. Receive a maturity score, radar analysis and actionable recommendations.',
    keywords:
      'real estate sales funnel analysis, developer GTM diagnostic, real estate CRM audit, developer pricing architecture, property sales conversion optimisation',
    canonical: 'https://ralrealadvisor.com/features',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "RAL Commercial Diagnostic",
      "description": "Commercial performance diagnostic for real estate developers covering strategy, marketing, sales, CRM, pricing and team performance.",
      "provider": {
        "@type": "Organization",
        "name": "RAL Real Advisor"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Commercial Diagnostic Areas",
        "itemListElement": [
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Strategy & Positioning Analysis"}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "GTM & Marketing System Evaluation"}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Sales Funnel & Conversion Analysis"}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "CRM & Data Discipline Review"}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Team Performance & KPI Evaluation"}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Pricing Architecture Assessment"}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Business Development & Channels"}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "After-Sales System Evaluation"}}
        ]
      }
    }
  },

  pricing: {
    title: 'Pricing | Real Estate Commercial Diagnostic Platform',
    description:
      'Flexible plans for real estate developers to assess commercial performance, analyse sales funnel efficiency and receive strategic recommendations and optimisation roadmaps.',
    keywords:
      'real estate performance software pricing, developer analytics platform pricing, commercial diagnostic pricing, real estate performance analytics platform',
    canonical: 'https://ralrealadvisor.com/pricing',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "RAL Real Advisor",
      "description": "Commercial performance diagnostic platform for real estate developers.",
      "brand": {
        "@type": "Brand",
        "name": "RAL Real Advisor"
      },
      "offers": [
        {
          "@type": "Offer",
          "name": "Project-Based Advisory",
          "description": "Focused support for a defined commercial challenge or initiative."
        },
        {
          "@type": "Offer",
          "name": "180-Day Transformation",
          "description": "A structured transformation model for rebuilding the commercial engine."
        },
        {
          "@type": "Offer",
          "name": "Monthly Advisory Retainer",
          "description": "Ongoing advisory support for continuous execution and performance improvement."
        }
      ]
    }
  },

  contact: {
    title: 'Contact RAL Real Advisor | Commercial Transformation for Developers',
    description:
      'Contact the RAL Real Advisor team to discuss commercial transformation, pricing strategy, GTM execution and performance optimisation for your real estate development business.',
    keywords:
      'real estate commercial advisory contact, developer strategy consulting, property developer performance consulting',
    canonical: 'https://ralrealadvisor.com/contact',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "RAL Real Advisor",
      "url": "https://ralrealadvisor.com",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "sales",
        "email": "info@raldiagnostic.com",
        "availableLanguage": ["English"]
      }
    }
  }
};


// SEO Component
const SEOHead = ({ page }) => {
  const config = SEO_CONFIG[page] || SEO_CONFIG.home;

  useEffect(() => {
    document.title = config.title;

    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }

      meta.setAttribute('content', content);
    };

    updateMeta('description', config.description);
    updateMeta('keywords', config.keywords);
    updateMeta('robots', 'index, follow');
    updateMeta('author', 'RAL Real Advisor');
    updateMeta('language', 'en');

    // Open Graph
    updateMeta('og:title', config.title, true);
    updateMeta('og:description', config.description, true);
    updateMeta('og:type', config.ogType, true);
    updateMeta('og:url', config.canonical, true);
    updateMeta('og:image', 'https://ralrealadvisor.com/og-image.jpg', true);
    updateMeta('og:site_name', 'RAL Real Advisor', true);

    // Twitter
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', config.title);
    updateMeta('twitter:description', config.description);
    updateMeta('twitter:image', 'https://ralrealadvisor.com/twitter-card.jpg');

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', config.canonical);

    // Structured Data
    let script = document.querySelector('#structured-data');

    if (!script) {
      script = document.createElement('script');
      script.id = 'structured-data';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(config.schema);

  }, [config]);

  return null;
};

export default SEOHead;